import { GameEntry } from './casino-feed';
import Config from './config';
import $ from "utils/net";

export class SlotTournamentsService {
    /**
     * Retrieves a paginated list of tournaments.
     * @param status - Filter by status ('active', 'upcoming', 'finished', 'all'). Defaults to 'active'.
     * @param page - Page number. Defaults to 1.
     * @param limit - Items per page. Defaults to 10.
     * @returns Promise resolving to the API response. Throws error on failure.
     */
    async listTournaments(
        status: string = 'active',
        page: number = 1,
        limit: number = 10
    ): Promise<ListApiResponse> {
        const queryParams = new URLSearchParams({
            status: status.toLowerCase(),
            page: "" + page,
            limit: "" + limit
        });
        const url = `${Config.softionAPI}/slot_tournaments/list?${queryParams.toString()}`;

        const response = await $.promiseGetJSON(url);
        return response as ListApiResponse;
    }

    /**
     * Retrieves details for a specific tournament, including the leaderboard.
     * @param id - The ID of the tournament.
     * @returns Promise resolving to the API response. Returns { status: 'error' } on specific failures like 400/404 or invalid ID. Throws for other network/server errors.
     */
    async getTournamentDetails(id: number): Promise<DetailsApiResponse> {
        const url = `${Config.softionAPI}/slot_tournaments/details/${id}`;

        const response = await $.promiseGetJSON(url);
        return response as DetailsApiResponse;
    }
}


// --- Interfaces ---

export type TournamentStatus = 'active' | 'upcoming' | 'finished' | 'cancelled';

export interface TournamentSummary {
    id: number;
    title: string;
    description: string;
    start_date: string; // ISO 8601 format
    end_date: string;   // ISO 8601 format
    status: TournamentStatus;
}

export interface PaginationInfo {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_records: number;
    filter_status: string;
}

export interface PrizeTier {
    rank: number;
    rank_to: number;
    amount: number;
    currency: string;
    type: string;
}

export interface RawSlotEntry {
    label: string,
    playUrl: string,
    provider: string,
    group: string,
    gameId: string,
    logoUrl: string
}
export type GameIconEntry = GameEntry & {
    brand: string,
    provider: string,
}

export function asGameEntry(slot: RawSlotEntry): GameIconEntry {
    return {
        id: slot.gameId,
        name: slot.label,
        logo: slot.logoUrl,
        category: slot.group,
        brand: slot.group,
        provider: slot.provider,
        tags: [],
        sort: 0
    };
}

export interface TournamentDetails {
    id: number;
    title: string;
    description: string;
    start_date: string; // ISO 8601 format
    end_date: string;   // ISO 8601 format
    status: TournamentStatus;
    winning_criteria: string;
    prize_structure: PrizeTier[];
    slots: RawSlotEntry[];
    minimal_bet: number | null;
    minimal_bet_currency: string | null;
    minimal_rounds: number | null;
    minimal_wager: number | null;
    minimal_wager_currency: string | null;
}

export interface LeaderboardEntry {
    rank: number;
    username: string; // Censored username
    score: number;
    is_current_user: boolean;
}

export interface ListApiResponse {
    data: TournamentSummary[];
    pagination: PaginationInfo;
}

export interface SuccessDetailsApiResponse {
    status: 'success';
    data: {
        tournament: TournamentDetails;
        leaderboard: LeaderboardEntry[];
    };
}

export interface ErrorDetailsApiResponse {
    status: 'error';
    data?: undefined; // Ensure data is not present on error
}

export type DetailsApiResponse = SuccessDetailsApiResponse | ErrorDetailsApiResponse;

